import React, { useEffect, useState } from 'react'
import { Modal, TreeSelect, Button, Tooltip } from 'antd'
import { useSelector, useDispatch } from 'react-redux'
import { DatePicker } from 'antd'
import moment from 'moment'
import { Link } from 'react-router-dom'
import styled, { createGlobalStyle } from 'styled-components'
import { fetchVenueOrders } from 'src/features/OrdersSlice'
import DownloadAllInfo from './DownloadAllInfo'
import { saveAs } from 'file-saver'
import { fetchAccountVenues } from 'src/features/AccountsSlice'
import CombinedFlashReport from './CombinedFlashReport'
import ShoppingListDownload from './ShoppingListDownload'
import Sage50Export from './Sage50Export'
import SupplierSpendAnalysisReport from './SupplierSpendAnalysisReport'
import AccrualsReport from './AccrualsReport'
import GpStockPerformanceReport, {
  GP_STOCK_PERFORMANCE_REPORT_NAME,
} from './GpStockPerformanceReport'
import ReportGroupSelect from './ReportGroupSelect'
import {
  ordersReportLargeGroupQuestion,
  ordersReportRowsWithEmptyVenues,
  shouldCloseOrdersReportAfterDownload,
  shouldConfirmOrdersReportSize,
} from './ordersReportRows'
import {
  VENUES_REPORT_DESCRIPTIONS,
  VENUES_REPORTS_INTRO,
} from './venuesReportDescriptions'

export const VENUES_REPORT_OPTIONS = [
  'Venues Orders Report',
  'Venue Report CSV',
  'Combined Flash Report',
  'Shopping List Download',
  'Sage 50 Export (CSV)',
  'Supplier Spend Analysis',
  'Product Purchasing Breakdown',
  'Accruals Report',
  GP_STOCK_PERFORMANCE_REPORT_NAME,
]

/** Wider than the old 40% so the GP form (helper + COS + Run) can be completed on a laptop. */
export const REPORTS_MODAL_WIDTH = '52%'
export const REPORTS_MODAL_WRAP_CLASS = 'venues-reports-modal'
/** Ant Design body: no nested scroll. The inner pane is the only overflow. */
export const REPORTS_MODAL_BODY_STYLE = {
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  maxHeight: 'calc(100vh - 96px)',
}
export const REPORTS_MODAL_SCROLL_STYLE = {
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
}

const ReportsModalLockStyle = createGlobalStyle`
  body.venues-reports-modal-open,
  html.venues-reports-modal-open {
    overflow: hidden !important;
  }
  .${REPORTS_MODAL_WRAP_CLASS}.ant-modal-wrap {
    overflow: hidden !important;
  }
  .${REPORTS_MODAL_WRAP_CLASS} .ant-modal {
    padding-bottom: 0;
    top: 24px;
  }
  .${REPORTS_MODAL_WRAP_CLASS} .ant-modal-content {
    display: flex;
    flex-direction: column;
    max-height: calc(100vh - 48px);
  }
  .${REPORTS_MODAL_WRAP_CLASS} .ant-modal-body {
    display: flex;
    flex-direction: column;
    min-height: 0;
    overflow: hidden;
  }
`

const VenuesReportModal = ({ visibleVenuesModal, setVsibleVenuesModal }) => {
  const dispatch = useDispatch()
  const venues = useSelector(state => state.accounts.accountVenues)
  const username = useSelector(state => state.auth.cognito.username)
  const [selectedVenueIds, setSelectedVenuesIds] = useState([])
  const [purchasingVenueId, setPurchasingVenueId] = useState(null)
  const dateFormat = 'DD/MM/YYYY'
  const venueOptions = venues?.map(item => ({
    title: item.title,
    value: item._id,
  }))
  const allIds = venueOptions?.map(({ value }) => value)
  const [date, setDate] = useState({
    start: moment().subtract(40, 'days').format(dateFormat),
    end: moment().format(dateFormat),
  })
  const [isLoading, setIsLoading] = useState(false)
  const onChangeDate = date => {
    setDate({ start: date[0], end: date[1] })
  }
  const reportOptions = VENUES_REPORT_OPTIONS
  const [selectedReport, setSelectedReport] = useState(null)
  const handleDownloadCSV = async () => {
    try {
      setIsLoading(true)
      const venueOrders = await fetchVenueOrders(
        Array.from(selectedVenueIds),
        date.start,
        date.end,
      )
      if (!venueOrders) return
      const selectedVenues = selectedVenueIds
        .map(id => venues.find(venue => String(venue._id) === String(id)))
        .filter(Boolean)
      const reportRows = ordersReportRowsWithEmptyVenues(
        venueOrders,
        selectedVenues,
      )
      const csvHeaders = headers.map(header => header.label).join(',') + '\n'
      const csvRows = reportRows
        .map(order => headers.map(header => order[header.key] || '').join(','))
        .join('\n')

      const csvContent = csvHeaders + csvRows
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      saveAs(blob, 'Venues-Orders-Report.csv')
      if (shouldCloseOrdersReportAfterDownload()) {
        setSelectedReport(null)
        setSelectedVenuesIds([])
        setVsibleVenuesModal(false)
      }
    } catch (error) {
      console.error('Error generating or downloading CSV:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const askThenDownloadOrders = () => {
    if (!shouldConfirmOrdersReportSize(selectedVenueIds.length)) {
      handleDownloadCSV()
      return
    }
    Modal.confirm({
      title: 'Large orders report',
      content: ordersReportLargeGroupQuestion(selectedVenueIds.length),
      okText: 'Generate report',
      cancelText: 'Cancel',
      onOk: () => handleDownloadCSV(),
    })
  }
  const headers = [
    { label: 'PO Number', key: 'PO' },
    { label: 'Title', key: 'Title' },
    { label: 'Sku', key: 'sku' },
    { label: 'Quantity', key: 'Quantity' },
    { label: 'Order Date', key: 'OrderDate' },
    { label: 'Item Price', key: 'ItemPrice' },
    { label: 'Total Price', key: 'TotalPrice' },
    { label: 'Venue', key: 'Venue' },
    { label: 'Ordered By', key: 'Buyer' },
    { label: 'Supplier Name', key: 'Supplier' },
    { label: 'Order Status', key: 'Status' },
  ]

  const treeData = reportOptions.map((report, index) => ({
    title: report,
    value: report,
    key: index,
  }))

  useEffect(() => {
    dispatch(fetchAccountVenues({ username }))
  }, [])

  useEffect(() => {
    if (!visibleVenuesModal) return undefined
    const body = document.body
    const root = document.documentElement
    body.classList.add('venues-reports-modal-open')
    root.classList.add('venues-reports-modal-open')
    return () => {
      body.classList.remove('venues-reports-modal-open')
      root.classList.remove('venues-reports-modal-open')
      // Drop leftover inline locks from antd Modal so other pages can scroll.
      body.style.removeProperty('overflow')
      root.style.removeProperty('overflow')
    }
  }, [visibleVenuesModal])

  return (
    <>
      <ReportsModalLockStyle />
      <Modal
        width={REPORTS_MODAL_WIDTH}
        wrapClassName={REPORTS_MODAL_WRAP_CLASS}
        bodyStyle={REPORTS_MODAL_BODY_STYLE}
        title={'Reports'}
        visible={visibleVenuesModal}
        onCancel={() => {
          setSelectedReport(null)
          setSelectedVenuesIds([])
          setPurchasingVenueId(null)
          setVsibleVenuesModal(false)
        }}
        footer={null}
      >
        <div
          data-testid='reports-modal-body'
          style={REPORTS_MODAL_SCROLL_STYLE}
        >
          <TreeSelect
            treeNodeFilterProp='title'
            showCheckedStrategy={TreeSelect.SHOW_CHILD}
            value={selectedReport}
            dropdownStyle={{ maxHeight: '300px' }}
            style={{ marginBottom: '15px', width: '355px' }}
            placeholder='Report Type'
            allowClear
            treeDefaultExpandAll
            onChange={value => setSelectedReport(value)}
            treeData={treeData}
          />
          {!selectedReport && (
            <HelperText data-testid='reports-modal-intro'>
              {VENUES_REPORTS_INTRO}
            </HelperText>
          )}
          {selectedReport && selectedReport === 'Venues Orders Report' && (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <HelperText data-testid='report-helper'>
                {VENUES_REPORT_DESCRIPTIONS['Venues Orders Report']}
              </HelperText>
              <ReportGroupSelect
                venues={venues}
                onPick={setSelectedVenuesIds}
              />
              <TreeSelect
                treeNodeFilterProp='title'
                allowClear={true}
                placeholder='Select Venues ...'
                treeCheckable={true}
                showCheckedStrategy={TreeSelect.SHOW_CHILD}
                dropdownStyle={{ maxHeight: '300px' }}
                style={{ marginBottom: '15px', width: '355px' }}
                onChange={ids => setSelectedVenuesIds(ids)}
                value={selectedVenueIds}
                treeData={[
                  {
                    title:
                      selectedVenueIds.length > 0 ? (
                        <span
                          onClick={() => setSelectedVenuesIds([])}
                          style={{
                            display: 'inline-block',
                            color: '#b5d922',
                            cursor: 'pointer',
                          }}
                        >
                          Unselect all
                        </span>
                      ) : (
                        <span
                          onClick={() => setSelectedVenuesIds(allIds)}
                          style={{
                            display: 'inline-block',
                            color: '#b5d922',
                            cursor: 'pointer',
                          }}
                        >
                          Select all
                        </span>
                      ),
                    value: 'xxx',
                    disableCheckbox: true,
                    disabled: true,
                  },
                  ...venueOptions,
                ]}
              />
              <StyledRangePicker
                defaultValue={[moment().subtract(40, 'days'), moment()]}
                onChange={(value, string) => onChangeDate(string)}
                format={dateFormat}
              />
              {selectedVenueIds.length === 0 ? (
                <Tooltip title={'Seleect at least one venue'}>
                  <Button
                    style={{ width: '355px', marginTop: 20 }}
                    className='ant-btn ant-btn-primary'
                  >
                    Download CSV Report
                  </Button>
                </Tooltip>
              ) : (
                <Button
                  loading={isLoading}
                  style={{ width: '355px', marginTop: 20 }}
                  className='ant-btn ant-btn-primary'
                  onClick={askThenDownloadOrders}
                  disabled={isLoading}
                >
                  Download CSV Report
                </Button>
              )}
            </div>
          )}
          {selectedReport && selectedReport === 'Venue Report CSV' && (
            <>
              <DownloadAllInfo venues={venues} />
            </>
          )}
          {selectedReport && selectedReport === 'Combined Flash Report' && (
            <>
              <CombinedFlashReport venues={venues} />
            </>
          )}
          {selectedReport && selectedReport === 'Shopping List Download' && (
            <>
              <ShoppingListDownload venues={venues} />
            </>
          )}
          {selectedReport && selectedReport === 'Sage 50 Export (CSV)' && (
            <>
              <Sage50Export venues={venues} />
            </>
          )}
          {selectedReport && selectedReport === 'Supplier Spend Analysis' && (
            <>
              <SupplierSpendAnalysisReport venues={venues} />
            </>
          )}
          {selectedReport === 'Product Purchasing Breakdown' && (
            <div>
              <HelperText>
                {VENUES_REPORT_DESCRIPTIONS['Product Purchasing Breakdown']}
              </HelperText>
              <TreeSelect
                treeNodeFilterProp='title'
                showSearch
                allowClear
                placeholder='Select a venue'
                value={purchasingVenueId || undefined}
                onChange={id => setPurchasingVenueId(id || null)}
                treeData={venueOptions}
                style={{ width: '100%', marginBottom: 15 }}
              />
              {purchasingVenueId ? (
                <Link
                  to={`/v/${purchasingVenueId}/purchasing/ordered`}
                  onClick={() => setVsibleVenuesModal(false)}
                >
                  <Button type='primary'>Open product purchasing report</Button>
                </Link>
              ) : (
                <Button type='primary' disabled>
                  Open product purchasing report
                </Button>
              )}
            </div>
          )}
          {selectedReport && selectedReport === 'Accruals Report' && (
            <>
              <AccrualsReport venues={venues} />
            </>
          )}
          {selectedReport &&
            selectedReport === GP_STOCK_PERFORMANCE_REPORT_NAME && (
              <>
                <GpStockPerformanceReport venues={venues} />
              </>
            )}
        </div>
      </Modal>
    </>
  )
}

const HelperText = styled.p`
  font-size: 14px;
  color: #666;
  margin-bottom: 10px;
`

const StyledRangePicker = styled(DatePicker.RangePicker)`
  .ant-calendar-picker-input.ant-input {
    height: 40px;
    padding: 6px 6px;
    font-size: 16px;
    width: 355px;
  }
`

export default VenuesReportModal
